import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  createTemplate as createTemplateApi,
  updateTemplate as updateTemplateApi,
  deleteTemplate as deleteTemplateApi,
  fetchTemplates,
  fetchTemplateById,
} from '../../lib/services/templateService';

import type {
  TemplateListItem,
  TemplatesState,
  CreateTemplatePayload,
  CreateTemplateResponse,
  TemplateDoc,
  UpdateTemplatePayload,
} from '../../lib/interfaces';

import { RootState } from '../types/sharedTypes';
import { reportError } from '../../lib/sentry';

type ResponseError = {
  response?: { data?: { message?: string } | string };
};

const getErrorMessage = (e: unknown): string => {
  if (typeof e === 'object' && e !== null && 'response' in e) {
    const data = (e as ResponseError).response?.data;
    if (typeof data === 'string') {
      return data;
    }
    return data?.message ?? 'Request failed';
  }
  if (e instanceof Error) {
    return e.message;
  }
  return 'Request failed';
};

function extractApiResult<T>(response: unknown): T | undefined {
  if (!response || typeof response !== 'object') return undefined;
  const dataObj =
    'data' in response ? (response as Record<string, unknown>).data : response;
  if (!dataObj || typeof dataObj !== 'object') return undefined;
  const resultObj =
    'result' in dataObj ? (dataObj as Record<string, unknown>).result : dataObj;
  return resultObj as T;
}

const initialState: TemplatesState = {
  items: [],
  selectedTemplateId: null,
  selectedTemplateBody: '',
  loading: false,
  error: null,
};

export const loadTemplates = createAsyncThunk<
  TemplateListItem[],
  void,
  { rejectValue: string }
>('templates/loadTemplates', async (_, thunkAPI) => {
  try {
    const res = await fetchTemplates();
    const payload = extractApiResult<
      TemplateListItem[] | { templates: TemplateListItem[] }
    >(res);

    if (Array.isArray(payload)) {
      return payload;
    }
    if (payload && 'templates' in payload && Array.isArray(payload.templates)) {
      return payload.templates;
    }

    return [];
  } catch (error) {
    reportError(error, { tags: { area: 'templates' } });
    return thunkAPI.rejectWithValue(getErrorMessage(error));
  }
});

export const loadTemplateBody = createAsyncThunk<
  { templateId: string; bodyMarkdown: string },
  string,
  { rejectValue: string }
>('templates/loadTemplateBody', async (templateId, thunkAPI) => {
  try {
    const res = await fetchTemplateById(templateId);
    const doc = extractApiResult<TemplateDoc>(res);

    if (!doc) throw new Error('Template not found');

    if (doc.bodyMarkdown) {
      return {
        templateId: doc._id ?? templateId,
        bodyMarkdown: doc.bodyMarkdown,
      };
    }

    const versions = Array.isArray(doc.versions) ? doc.versions : [];
    const byCurrent = versions.find((v) => v?.v === doc.currentVersion) ?? null;
    const byLast = versions.length ? versions[versions.length - 1] : null;
    const chosen = byCurrent ?? byLast;
    const bodyMarkdown = chosen?.bodyMarkdown ?? '';

    return { templateId: doc._id ?? templateId, bodyMarkdown };
  } catch (error) {
    reportError(error, { tags: { area: 'templates' } });
    return thunkAPI.rejectWithValue(getErrorMessage(error));
  }
});

export const createTemplate = createAsyncThunk<
  CreateTemplateResponse & { request: CreateTemplatePayload },
  CreateTemplatePayload,
  { rejectValue: string }
>('templates/createTemplate', async (payload, thunkAPI) => {
  try {
    const res = await createTemplateApi(payload);
    const data = extractApiResult<CreateTemplateResponse>(res);

    if (!data) throw new Error('Invalid response from server');
    return { ...data, request: payload };
  } catch (error) {
    reportError(error, { tags: { area: 'templates' } });
    return thunkAPI.rejectWithValue(getErrorMessage(error));
  }
});

export const updateTemplate = createAsyncThunk<
  TemplateDoc,
  UpdateTemplatePayload,
  { rejectValue: string }
>('templates/updateTemplate', async ({ id, body }, thunkAPI) => {
  try {
    const res = await updateTemplateApi(id, body);
    const doc = extractApiResult<TemplateDoc>(res);

    if (!doc) throw new Error('Invalid response from server');
    return doc;
  } catch (error) {
    reportError(error, { tags: { area: 'templates' } });
    return thunkAPI.rejectWithValue(getErrorMessage(error));
  }
});

export const removeTemplate = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('templates/deleteTemplate', async (id, thunkAPI) => {
  try {
    await deleteTemplateApi(id);
    return id;
  } catch (error) {
    reportError(error, { tags: { area: 'templates' } });
    return thunkAPI.rejectWithValue(getErrorMessage(error));
  }
});

const templatesSlice = createSlice({
  name: 'templates',
  initialState,
  reducers: {
    clearSelectedTemplate(state) {
      state.selectedTemplateId = null;
      state.selectedTemplateBody = '';
    },
    setSelectedTemplateBody(state, action: PayloadAction<string>) {
      state.selectedTemplateBody = action.payload;
    },
    setSelectedTemplateId(state, action: PayloadAction<string | null>) {
      state.selectedTemplateId = action.payload ?? null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        loadTemplates.fulfilled,
        (state, action: PayloadAction<TemplateListItem[]>) => {
          state.loading = false;
          state.items = action.payload ?? [];
        }
      )
      .addCase(
        loadTemplates.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload ?? 'Failed to load templates';
        }
      )

      .addCase(
        loadTemplateBody.fulfilled,
        (
          state,
          action: PayloadAction<{ templateId: string; bodyMarkdown: string }>
        ) => {
          state.selectedTemplateId = action.payload.templateId;
          state.selectedTemplateBody = action.payload.bodyMarkdown;
        }
      )
      .addCase(
        loadTemplateBody.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.error = action.payload ?? 'Failed to load template body';
        }
      )

      .addCase(createTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createTemplate.fulfilled,
        (
          state,
          action: PayloadAction<
            CreateTemplateResponse & { request: CreateTemplatePayload }
          >
        ) => {
          state.loading = false;
          const { id } = action.payload;
          const { name, bodyMarkdown, category, language } =
            action.payload.request;

          const newItem: TemplateListItem = {
            _id: id,
            name,
            category: category ?? undefined,
            language,
            bodyMarkdown,
          };

          const idx = state.items.findIndex((t) => t._id === id);
          if (idx >= 0) state.items[idx] = { ...state.items[idx], ...newItem };
          else state.items.unshift(newItem);

          state.selectedTemplateId = id;
          state.selectedTemplateBody = bodyMarkdown;
        }
      )
      .addCase(
        createTemplate.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload ?? 'Failed to create template';
        }
      )

      .addCase(updateTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateTemplate.fulfilled,
        (state, action: PayloadAction<TemplateDoc>) => {
          state.loading = false;
          const doc = action.payload;
          const id = doc._id;

          const idx = state.items.findIndex((t) => t._id === id);
          const latestBody =
            doc.bodyMarkdown ??
            doc.versions?.find((v) => v.v === doc.currentVersion)
              ?.bodyMarkdown ??
            doc.versions?.[doc.versions.length - 1]?.bodyMarkdown;

          const patch: Partial<TemplateListItem> = {
            name: doc.name,
            category: doc.category ?? undefined,
            language: doc.language,
            bodyMarkdown: latestBody,
          };

          if (idx >= 0) state.items[idx] = { ...state.items[idx], ...patch };

          if (state.selectedTemplateId === id && latestBody) {
            state.selectedTemplateBody = latestBody;
          }
        }
      )
      .addCase(removeTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        removeTemplate.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.loading = false;
          const id = action.payload;
          state.items = state.items.filter((t) => t._id !== id);

          if (state.selectedTemplateId === id) {
            state.selectedTemplateId = null;
            state.selectedTemplateBody = '';
          }
        }
      )
      .addCase(
        removeTemplate.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload ?? 'Failed to delete template';
        }
      )
      .addCase(
        updateTemplate.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload ?? 'Failed to update template';
        }
      );
  },
});

export const {
  clearSelectedTemplate,
  setSelectedTemplateBody,
  setSelectedTemplateId,
} = templatesSlice.actions;

export default templatesSlice.reducer;

export const selectTemplates = (s: RootState) => s.templates.items;
export const selectTemplatesLoading = (s: RootState) => s.templates.loading;
export const selectSelectedTemplateId = (s: RootState) =>
  s.templates.selectedTemplateId;
export const selectSelectedTemplateBody = (s: RootState) =>
  s.templates.selectedTemplateBody;
